import { BackendApiErrorDto, SingleBackendApiErrorDto } from './apiErrorDto';
import { ApiErrorTranslationKeys } from '@/lib/api/apiErrorTranslationKeys.ts';

// IF YOU NEED TO HANDLE ERROR MESSAGES IN YOUR APPLICATION ADD THEM HERE
export enum BackendErrorNames {
  /** Error name was missing might happen for 403 */
  UnknownApiError = 'UnknownApiError',
  MultipleApiErrors = 'MultipleApiErrors',
  UserNotFoundException = 'UserNotFoundException',
  BadVerificationCodeException = 'BadVerificationCodeException',
  OfficesLimitReachedException = 'OfficesLimitReachedException',
  CannotBuySmsPackageWithoutActiveSubscriptionException = 'CannotBuySmsPackageWithoutActiveSubscriptionException',
  CannotBuySmsPackageWhenCardDetailsAreNotGivenException = 'CannotBuySmsPackageWhenCardDetailsAreNotGivenException',
}

const transformSingleErrorToReadableMessage = (
  singleBackendApiErrorDto: SingleBackendApiErrorDto,
): string => {
  // Transform error to readable message. If transformation is unknown use backend message
  switch (singleBackendApiErrorDto.errorName) {
    case BackendErrorNames.UserNotFoundException:
      return 'Użytkownik nie istnieje lub nie masz do niego dostępu.';
    case BackendErrorNames.BadVerificationCodeException:
      return 'Podany został zły kod.';
    case BackendErrorNames.OfficesLimitReachedException:
      return 'Nie można utworzyć nowego gabientu - osiągnięto limit.';
    case BackendErrorNames.CannotBuySmsPackageWithoutActiveSubscriptionException:
      return 'Nie mozna kupić pakietu SMS bez aktywnej subskrypcji.';
    case BackendErrorNames.CannotBuySmsPackageWhenCardDetailsAreNotGivenException:
      return 'Nie można kupić pakietu SMS bez podania danych karty. W celu dodania danych karty przejdź do zakładki "Ustawienia" i wybierz zakładke "Subskrybcja".';
    default:
      return getMessageUsingTranslationKey(singleBackendApiErrorDto);
  }
};

const getMessageUsingTranslationKey = (
  singleBackendApiErrorDto: SingleBackendApiErrorDto,
) => {
  //For errors without specific error key - use error message because has more meaning
  if (
    singleBackendApiErrorDto.translationKey ===
    ApiErrorTranslationKeys.INVALID_DATA
  ) {
    return singleBackendApiErrorDto.errorMessage;
  }
  return (
    ApiErrorTranslationKeys[singleBackendApiErrorDto.translationKey] ??
    singleBackendApiErrorDto.errorMessage
  );
};

// BELOW IS ALL THE HANDLING. IF YOU THINK YOU NEED TO CHANGE IT PLEASE RE-THINK IT :)
/**
 * Represents exceptions thrown by backend not machining BackendApiErrorDto.
 * Usually not expected by backend developer (error from spring security, db constraints etc)
 * For this kind of exception we show general message. You should not write custom messages for this exception. Ask backed developer to handle it on backend and provide exception BackendApiErrorDto to you.
 */
export class BackendApiError extends Error {
  statusCode: number;

  constructor(statusCode: number) {
    super(); // todo error while loging out ???
    this.name = BackendErrorNames.UnknownApiError;
    this.message = getUnknownErrorMessage(statusCode);
    this.statusCode = statusCode;
  }
}

/**
 * Exceptions thrown by backend api usually planed by backend dev ( either our custom exceptions or exceptions from dto validation)
 */
export class KnownBackendApiError extends BackendApiError {
  private apiErrorDto: BackendApiErrorDto;

  constructor(apiErrorDto: BackendApiErrorDto, statusCode: number) {
    super(statusCode);
    this.apiErrorDto = apiErrorDto;
    this.message = transfromErrorDtoToReadableMessage(apiErrorDto, statusCode);
    this.name = transformErrorDtoToErrorName(apiErrorDto);
  }

  /**
   * Returns whole dto with errors. Can be used to show toastComponent with custom message
   */
  getApiErrorDto(): BackendApiErrorDto {
    return this.apiErrorDto;
  }

  /**
   * Checks whether the backend returned a specific error key. Matches against the
   * error map key, `errorName` and `translationKey` so callers can react to a known
   * key regardless of which field the backend populated it in.
   *
   * Pass the backend `TranslationKey` *enum member name* (`BOOKING_PRICE_CHANGED`),
   * not its dotted label (`error.booking.price_changed`). The dotted form is an
   * internal backend annotation that never reaches the wire: `TranslationKey` has
   * no `@JsonValue`, so Jackson serializes `translationKey` via `name()`.
   */
  hasErrorKey(errorKey: string): boolean {
    return Object.entries(this.apiErrorDto).some(
      ([key, value]) =>
        key === errorKey ||
        value.errorName === errorKey ||
        value.translationKey === errorKey,
    );
  }
}

/**
 * Type guard that returns true when the given error is a backend error carrying a
 * specific known error key.
 */
export const isKnownBackendErrorKey = (
  error: unknown,
  errorKey: string,
): boolean =>
  error instanceof KnownBackendApiError && error.hasErrorKey(errorKey);

/**
 * Transform BackendApiErrorDto to readable message by going over each error
 */
const transfromErrorDtoToReadableMessage = (
  backErrorDto: BackendApiErrorDto,
  statusCode: number,
): string => {
  // If there are errors messages transform then to readable messages
  // If dto is empty return unknown error message (this should not happen)
  if (Object.keys(backErrorDto).length > 0) {
    let message = '';
    Object.values(backErrorDto).forEach((value) => {
      message += `${transformSingleErrorToReadableMessage(value)} \n`;
    });
    return message;
  } else {
    return getUnknownErrorMessage(statusCode);
  }
};

const transformErrorDtoToErrorName = (
  backErrorDto: BackendApiErrorDto,
): string => {
  // This might be used to determine what kind of error is thrown it handle special message for it
  if (Object.keys(backErrorDto).length == 0) {
    return BackendErrorNames.UnknownApiError;
  }
  if (Object.keys(backErrorDto).length == 1) {
    return Object.values(backErrorDto)[0].errorName;
  }
  return BackendErrorNames.MultipleApiErrors;
};

export class FirebaseApiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FirebaseError';
  }
}

export const getUnknownErrorMessage = (statusCode: number): string => {
  return `Żądanie zostało odrzucone z kodem błędu ${statusCode}`;
};

export class UnauthenticatedError extends Error {
  constructor() {
    super('Brak tokenu uwierzytelniającego. Zaloguj się ponownie.');
    this.name = 'UnauthenticatedError';
  }
}

export class MissingTenantError extends Error {
  constructor() {
    super(
      'Brak identyfikatora organizacji. Odśwież stronę i spróbuj ponownie.',
    );
    this.name = 'MissingTenantError';
  }
}
